import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import type { UseNavigateResult } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useMe } from '@/api/me';
import { useMyTenants } from '@/api/tenant';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { useFlag } from '@/hooks/use-flag';
import {
  ProjectCatalogPresets,
  type ProjectCatalogPresetSelection,
} from '@/features/asset-catalog/project-catalog-presets';
import { useCreateEstate } from '@/features/estates/api/estates';
import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import type { UpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateAreas } from '@/features/estates-property/api/estate-areas';
import { useUpdateEstateDetails } from '@/features/estates-property/api/estate-details';
import type { Project } from '@/features/projects/api/projects';
import { getProject } from '@/features/projects/api/projects';
import { useCreateTenantProject } from '@/features/tenants/api/tenant-projects';
import type { AtLeastOne } from '@/utils/types/helpers';
import { EstateFormDialog } from './estate-form-dialog';
import type { useModal } from './new/use-modal';

export interface CreateEstateModalProps {
  navigate: UseNavigateResult<string>;
  onSuccess?: (object: AtLeastOne<{ estateId?: string; projectId?: string }>) => void;
  modal: ReturnType<typeof useModal>;
}

const emptyAddressData: EstateSchemaType['addressData'] = {
  address: '',
  addressAutoComplete: '',
  postalCode: '',
  city: '',
  countryId: '',
  street: '',
  houseNumber: '',
};

const getProjectAddressFormValues = (
  project: Project
): Partial<EstateSchemaType['addressData']> => {
  const address = project.address;

  return {
    ...(address?.address && {
      address: address.address,
      addressAutoComplete: address.address,
    }),
    ...(address?.city && { city: address.city }),
    ...(address?.country?.id && { countryId: address.country.id }),
    ...(address?.postalCode && { postalCode: address.postalCode }),
    street: address?.street || '',
    houseNumber: address?.houseNumber || '',
    ...(address?.lat != null && { lat: address.lat }),
    ...(address?.long != null && { long: address.long }),
  };
};

const getEstateDetailsPayload = (estateId: string, project: Project) => {
  if (!project.details) return null;

  return {
    id: estateId,
    constructionType: '',
    condition: '',
    buildingYear: project.details.buildingYear || '',
    heatingType: [],
    heatGeneration: '',
    marketType: '',
    usageType: project.details.usageType || '',
    typeOfGlass: '',
    flooring: '',
    propertyType: '',
  };
};

const getEstateAreasPayload = (estateId: string, project: Project): UpdateEstateAreas | null => {
  if (!project.areaRooms) return null;

  const estateAreasPayload: UpdateEstateAreas = {
    id: estateId,
    totalNumberOfRooms: 0,
  };

  if (project.areaRooms.hasElevator !== null && project.areaRooms.hasElevator !== undefined) {
    estateAreasPayload.hasElevator = project.areaRooms.hasElevator;
  }

  return estateAreasPayload;
};

const getEstateDataWithProjectInheritance = (
  estateData: EstateSchemaType['estateData'],
  project: Project
): EstateSchemaType['estateData'] => ({
  ...estateData,
  ...(project.energyDetails?.id && {
    estateEnergyDetailsId: project.energyDetails.id,
  }),
});

export const CreateEstateModal: React.FC<CreateEstateModalProps> = ({
  navigate,
  onSuccess,
  modal,
}) => {
  const schema = getCreateEstateSchema({ isAddressRequired: false });
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: tenants } = useMyTenants();
  const isAssetCatalogEnabled = useFlag('asset-catalog');
  const projectDataRef = useRef<Project | null>(null);
  const [catalogPresets, setCatalogPresets] = useState<{
    tenantId: string;
    value: ProjectCatalogPresetSelection;
  }>({ tenantId: '', value: {} });
  const queryClient = useQueryClient();
  const { mutateAsync: updateEstateDetails } = useUpdateEstateDetails({});

  const { mutateAsync: updateEstateAreas } = useUpdateEstateAreas({});

  const defaultValues: EstateSchemaType = useMemo(
    () => ({
      estateData: {
        name: '',
        propertyType: '',
        marketType: '',
        availability: null,
        tenantId: tenants[0]?.id ?? '',
        projectId: undefined,
        creatorId: me?.id,
        propertyLevel: PROPERTY_LEVELS.OBJECT,
      },
      addressData: emptyAddressData,
    }),
    [me?.id, tenants]
  );

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { mutateAsync: createEstateAsync } = useCreateEstate({
    mutationConfig: {
      onSuccess: async ([createdEstate]) => {
        setCatalogPresets({ tenantId: '', value: {} });
        const projectData = projectDataRef.current;
        if (projectData && createdEstate.projectId) {
          const promises: Promise<unknown>[] = [];

          const estateDetailsPayload = getEstateDetailsPayload(createdEstate.id, projectData);
          if (estateDetailsPayload) {
            promises.push(updateEstateDetails(estateDetailsPayload));
          }

          const estateAreasPayload = getEstateAreasPayload(createdEstate.id, projectData);
          if (estateAreasPayload && 'hasElevator' in estateAreasPayload) {
            promises.push(updateEstateAreas(estateAreasPayload));
          }

          await Promise.all(promises);
        }
        if (onSuccess) {
          onSuccess({ estateId: createdEstate.id });
        } else {
          navigate({ to: '/estates/$estateId', params: { estateId: createdEstate.id } });
        }
        queryClient.invalidateQueries({ queryKey: ['estates'] });
        methods.reset(defaultValues);
        toast.success(t('create-estate-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const { mutateAsync: createProjectAsync } = useCreateTenantProject({
    mutationConfig: {
      onSuccess: ([createdProject]) => {
        setCatalogPresets({ tenantId: '', value: {} });
        if (onSuccess) {
          onSuccess({ projectId: createdProject.id });
        } else {
          navigate({ to: '/projects/$projectId', params: { projectId: createdProject.id } });
        }
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['estates'] });
        methods.reset(defaultValues);
        toast.success(t('create-project-success'));
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const projectId = methods.watch('estateData.projectId') || '';
  const tenantId = methods.watch('estateData.tenantId') || '';
  const propertyLevel = methods.watch('estateData.propertyLevel');
  const selectedCatalogPresets = catalogPresets.tenantId === tenantId ? catalogPresets.value : {};

  const createProjectFromForm = async (formValues: EstateSchemaType) => {
    const { availability: _, ...projectData } = formValues.estateData;
    await createProjectAsync({
      projectData: {
        ...projectData,
        status: 'draft',
        isActive: 0,
        creatorId: me?.id,
      },
      addressData: {
        ...formValues.addressData,
      },
      tenantId: formValues.estateData.tenantId,
      ...(isAssetCatalogEnabled && { assetCatalogPresets: selectedCatalogPresets }),
    });
  };

  const createEstateFromForm = async (formValues: EstateSchemaType) => {
    if (!formValues.estateData.projectId) {
      await createEstateAsync(formValues);
      return;
    }

    const projectData = await getProject(formValues.estateData.projectId);
    projectDataRef.current = projectData;

    await createEstateAsync({
      ...formValues,
      estateData: getEstateDataWithProjectInheritance(formValues.estateData, projectData),
    });
  };

  useEffect(() => {
    if (!projectId) {
      methods.reset((prev) => ({
        ...prev,
        addressData: emptyAddressData,
      }));
      return;
    }

    let isCurrent = true;

    getProject(projectId)
      .then((projectData) => {
        if (!isCurrent) return;

        methods.reset((prev) => ({
          ...prev,
          addressData: {
            ...prev.addressData,
            ...getProjectAddressFormValues(projectData),
          },
        }));
      })
      .catch(() => undefined);

    return () => {
      isCurrent = false;
    };
  }, [methods, projectId]);

  const onSubmit = async (args: EstateSchemaType) => {
    projectDataRef.current = null;

    if (args.estateData.tenantId && args.estateData.propertyLevel === PROPERTY_LEVELS.PROJECT) {
      await createProjectFromForm(args);
      return;
    }

    await createEstateFromForm(args);
    queryClient.invalidateQueries({ queryKey: ['projects', 'estates', projectId] });
  };

  return (
    <EstateFormDialog modal={modal} title={t('createNewEstateProject')}>
      <FormProvider {...methods}>
        <EstateForm
          label={t('create')}
          onSubmit={onSubmit}
          onCancel={modal.dismiss}
          closeModal={modal.dismiss}
          options={{ newUi: true }}
        >
          {isAssetCatalogEnabled && propertyLevel === PROPERTY_LEVELS.PROJECT && tenantId && (
            <ProjectCatalogPresets
              key={tenantId}
              tenantId={tenantId}
              value={selectedCatalogPresets}
              onChange={(value) => setCatalogPresets({ tenantId, value })}
            />
          )}
        </EstateForm>
      </FormProvider>
    </EstateFormDialog>
  );
};
